import classNames from "classnames";
import {useEffect, useState} from "react";
import {DateRange} from "react-day-picker";
import {FiFilter} from "react-icons/fi";

import {Button} from "@/components/button/Button.tsx";
import {RequiredDateRange} from "@/components/datetime/utils.ts";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import {Icon} from "@/components/icon/Icon.tsx";

import {
  FilterCategory,
  FilterCategoryType,
  FilterDropdownProps,
  FilterSets,
  FilterValueType,
  getFilterValue,
  MatchMode,
  RangeFilterType,
  withMatchMode,
} from "./FilterDropdown.ts";
import {RightPaneFactory} from "./rightPane/RightPaneFactory";

import styles from "./FilterDropdown.module.scss";

const getDefaultValue = <C extends FilterCategory>(category: C): FilterValueType<C> => {
  switch (category.type) {
    case FilterCategoryType.RANGE:
      return null as FilterValueType<C>;
    case FilterCategoryType.ARRAY:
      return [] as unknown as FilterValueType<C>;
    case FilterCategoryType.GQL:
      return [] as unknown as FilterValueType<C>;
    default:
      throw new Error(`Unknown category type: ${category}`);
  }
};

export const FilterDropdown = <C extends FilterCategory>({
  categories,
  onApply,
  appliedFilterSets,
  loading,
  defaultActiveCategoryId = "",
  portal,
  disabled,
}: FilterDropdownProps<C>) => {
  const [tempFilterSets, setTempFilterSets] = useState<FilterSets<C>>(appliedFilterSets);
  const [activeCategoryId, setActiveCategoryId] = useState<string>(defaultActiveCategoryId || categories[0]?.key || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [show, setShow] = useState(false);
  const [categoryTotals, setCategoryTotals] = useState<{[key: string]: number | null}>({});

  useEffect(() => {
    setTempFilterSets(appliedFilterSets);
  }, [appliedFilterSets]);

  const totalApplied = categories.reduce((acc, cat) => {
    const value = getFilterValue<C>(appliedFilterSets, cat.key);
    if (cat.type === FilterCategoryType.RANGE) {
      const rangeValue = value as RangeFilterType | undefined;
      return acc + (rangeValue?.from || rangeValue?.to ? 1 : 0);
    }
    const selectedForCategory = (value as string[]) || [];
    return acc + selectedForCategory.length;
  }, 0);

  const totalSelected = categories.reduce((acc, cat) => {
    const value = getFilterValue<C>(tempFilterSets, cat.key);
    if (cat.type === FilterCategoryType.RANGE) {
      const rangeValue = value as RequiredDateRange;
      return acc + (rangeValue?.from || rangeValue?.to ? 1 : 0);
    }
    const selectedForCategory = (value as string[]) || [];
    return acc + selectedForCategory.length;
  }, 0);

  const handleToggle = (nextShow: boolean) => {
    setShow(nextShow);
    if (nextShow) {
      setSearchQuery("");
    }
  };

  const handleSelectCategory = (categoryId: string) => {
    setActiveCategoryId(categoryId);
    setSearchQuery("");
  };

  const handleToggleOption = (categoryId: string, optionValue: string) => {
    setTempFilterSets((prev: FilterSets<C>) => {
      const currentSelections = (getFilterValue<C>(prev, categoryId) as string[]) || [];
      if (currentSelections.includes(optionValue)) {
        return {
          ...prev,
          [categoryId]: currentSelections.filter((key) => key !== optionValue),
        };
      } else {
        return {
          ...prev,
          [categoryId]: [...currentSelections, optionValue],
        };
      }
    });
  };

  const handleApply = () => {
    onApply(tempFilterSets);
    setShow(false);
  };

  const handleResetAll = () => {
    const resetFilterSets = {} as FilterSets<C>;

    categories.forEach((cat) => {
      resetFilterSets[cat.key as keyof typeof appliedFilterSets] = getDefaultValue(cat);
    });

    setTempFilterSets(resetFilterSets);
    onApply({});
    setShow(false);
  };

  const handleDateRangeChange = (categoryId: string, range: DateRange | undefined) => {
    setTempFilterSets((prev: FilterSets<C>) => ({
      ...prev,
      [categoryId]: range,
    }));
  };

  const handleMatchModeChange = (categoryId: string, mode: MatchMode) => {
    setTempFilterSets((prev: FilterSets<C>) => withMatchMode(prev, categoryId, mode));
  };

  const handleCancel = () => {
    setTempFilterSets(appliedFilterSets);
    setShow(false);
  };

  const activeCategory = categories.find((cat) => cat.key === activeCategoryId);

  // Applying before the options resolve would submit whatever placeholder values the caller is
  // holding, so the actions stay disabled until they arrive.
  const actionsDisabled = loading || (totalSelected === 0 && totalApplied === 0);

  return (
    <Dropdown show={show} onToggle={handleToggle}>
      <Dropdown.Toggle
        onClick={() => {
          setShow(true);
        }}
        variant="secondary"
        className={classNames(styles.filterToggle, "text-nowrap")}
        disabled={disabled}
      >
        {totalApplied > 0 ? `Filters (${totalApplied})` : "Filters"} <Icon icon={FiFilter} />
      </Dropdown.Toggle>

      <Dropdown.Menu className={classNames(styles.dropdownMenu, {[styles.portal]: portal})} portal={portal}>
        {loading ? (
          <div className={styles.loadingPane} role="status">
            Loading filters…
          </div>
        ) : (
          <div className={styles.mainContent}>
            <div className={styles.categoryList}>
              {categories.map((cat) => {
                const selectedCount =
                  cat.type === FilterCategoryType.RANGE
                    ? (getFilterValue<C>(tempFilterSets, cat.key) as RequiredDateRange)?.from ||
                      (getFilterValue<C>(tempFilterSets, cat.key) as RequiredDateRange)?.to
                      ? 1
                      : 0
                    : ((getFilterValue<C>(tempFilterSets, cat.key) as string[]) || []).length;

                return (
                  <div
                    key={cat.key}
                    className={
                      cat.key === activeCategoryId ? `${styles.categoryItem} ${styles.active}` : styles.categoryItem
                    }
                    onClick={() => handleSelectCategory(cat.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelectCategory(cat.key);
                        e.preventDefault();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className={styles.categoryName}>{cat.display}</span>
                    {selectedCount > 0 && <span className={styles.categoryNumbers}>({selectedCount})</span>}
                  </div>
                );
              })}
            </div>

            {activeCategory && (
              <RightPaneFactory
                // a different category is a different pane, so it starts fresh rather than
                // inheriting the previous one's debounced search and already-fetched options
                key={activeCategoryId}
                category={activeCategory}
                totalOptions={categoryTotals[activeCategoryId]}
                setTotalOptions={(total) => {
                  setCategoryTotals((prev) => ({...prev, [activeCategoryId]: total}));
                }}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                localFilterSets={tempFilterSets}
                onToggleOption={handleToggleOption}
                onChange={handleDateRangeChange}
                onMatchModeChange={handleMatchModeChange}
              />
            )}
          </div>
        )}

        <div className={styles.footer}>
          <Button variant="ghost" className={"p-0"} disabled={actionsDisabled} onClick={handleResetAll}>
            Reset All
          </Button>
          <div className={"d-flex"}>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button style={{marginLeft: "0.5rem"}} disabled={actionsDisabled} onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};
